/**
 * Porte de l'accueil (`/`) — FND-DEMO-ADMIN-01 / HD-35.
 *
 * Un porteur SANS appartenance n'a aucun espace actif : l'accueil ne doit ni emprunter un espace
 * de l'organisation (chaque appel scoped serait refusé 403), ni afficher un tableau de bord vide
 * sans explication. Deux issues, toujours nommées :
 *  - il peut administrer (`canAccessAdministration`) → Accueil administration (lot NAV 0.5.x) ;
 *  - sinon → état vide explicite : « aucun espace ne vous est attribué — demandez une invitation ».
 *
 * Tant que la liste charge, ou si aucun espace n'existe encore (première installation), ou si le
 * porteur a au moins un espace accessible, l'accueil habituel est rendu tel quel.
 */
import type { JSX, ReactNode } from 'react'
import { Navigate } from 'react-router-dom'

import { canAccessAdministration, type Session } from '@/auth/session'
import { EmptyState } from '@/components/ErrorState'
import { InvitationsRecues } from '@/components/InvitationsRecues'
import { PageHeader } from '@/components/PageHeader'
import { useWorkspace } from '@/hooks/useWorkspace'
import { useT } from '@/i18n/language'

export function PorteAccueil({ session, children }: { session: Session; children: ReactNode }): JSX.Element {
  const { t } = useT()
  const { workspaces, accessibles, isLoading, error } = useWorkspace()
  // Revue de sécurité (CRITIQUE-FND-053-060) : la passerelle ne liste plus à un non-administrateur
  // que SES espaces — une liste vide ne distingue donc plus « organisation vide » de « aucun espace
  // attribué ». Sans droit d'administration, c'est toujours le second cas qu'on dit.
  const sansEspaceAttribue = !isLoading && !error && accessibles.length === 0
    && (workspaces.length > 0 || !canAccessAdministration(session))
  if (!sansEspaceAttribue) return <>{children}</>
  // Lot NAV 0.5.x (registre n° 12) : l'administrateur sans projet arrive sur SON accueil, plus sur
  // les Réglages d'un projet qu'il n'a pas.
  if (canAccessAdministration(session)) return <Navigate to="/administration" replace />
  return (
    <section className="space-y-6" data-testid="accueil-sans-espace">
      <PageHeader title={t('dashboard.title', 'Tableau de bord')} />
      <EmptyState title={t('accueil.sansEspaceTitre', 'Aucun espace ne vous est attribué')}
                  hint={t('accueil.sansEspaceHint', 'Demandez une invitation à un propriétaire d’espace : vous verrez alors ses projets ici.')}
                  sansAction="amont" annoncer />
      {/* FND-V050-050 : l'invité sans espace voit ici l'invitation qui l'attend, et la rejoint. */}
      <InvitationsRecues discret />
    </section>
  )
}
